import { FC, useCallback, useEffect, useState } from 'react';

import { Button, Modal, TextBlock } from '@skillarea/lib-frontend';
import { useLocation, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import cn from 'classnames';

import styles from './PaymentResultModal.module.scss';

import { centsToUsd, pushGa4Event } from '~/lib/analytics/gtm';
import { playDepositCreditedSound } from '~/lib/game-sounds';
import {
  PAYMENT_FAILURE_HASHES,
  PAYMENT_SUCCESS_HASH,
  PENDING_DEPOSIT_STORAGE_KEY,
  clearCryptoDepositAmountHintForTransactionId,
  getCryptoInvoiceIdFromPaymentReturnHash,
  getNormalizedPaymentHash,
  tryConsumeCryptoDepositAmountForInvoiceReturn,
} from '~/lib/billing/payment-return';

/** One purchase push per return URL (Strict Mode remount + pending vs hash fallback). */
const GA4_DEPOSIT_PURCHASE_DEDUPE_PREFIX = 'ga4:deposit-purchase-sent:';

export const PaymentResultModal: FC = () => {
  const { t } = useTranslation('modals');
  const location = useLocation();
  const navigate = useNavigate();
  const [currentHash, setCurrentHash] = useState<string>('');

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const updateHash = () => {
      setCurrentHash(getNormalizedPaymentHash(window.location.hash));
    };

    updateHash();
    window.addEventListener('hashchange', updateHash);
    window.addEventListener('popstate', updateHash);

    return () => {
      window.removeEventListener('hashchange', updateHash);
      window.removeEventListener('popstate', updateHash);
    };
  }, []);

  const isSuccess = currentHash === PAYMENT_SUCCESS_HASH;
  const isFailure = PAYMENT_FAILURE_HASHES.includes(
    currentHash as (typeof PAYMENT_FAILURE_HASHES)[number],
  );
  const isOpen = isSuccess || isFailure;

  useEffect(() => {
    if (!isSuccess || typeof window === 'undefined') {
      return;
    }

    void playDepositCreditedSound();

    const dedupeKey = `${GA4_DEPOSIT_PURCHASE_DEDUPE_PREFIX}${window.location.pathname}${window.location.search}${window.location.hash}`;
    if (window.sessionStorage.getItem(dedupeKey) === '1') {
      return;
    }

    const markPurchaseAnalyticsSent = (): void => {
      window.sessionStorage.setItem(dedupeKey, '1');
    };

    const pendingStr = window.localStorage.getItem(PENDING_DEPOSIT_STORAGE_KEY);
    if (pendingStr) {
      window.localStorage.removeItem(PENDING_DEPOSIT_STORAGE_KEY);
      try {
        const pending = JSON.parse(pendingStr) as {
          transaction_id?: string;
          valueInCents?: number;
          currency?: string;
        };
        if (pending.transaction_id) {
          clearCryptoDepositAmountHintForTransactionId(pending.transaction_id);
        }
        const pendingCents = pending.valueInCents;
        const valueMajor =
          pendingCents != null && Number.isFinite(pendingCents)
            ? centsToUsd(pendingCents)
            : 0;
        pushGa4Event('purchase', {
          transaction_id: pending.transaction_id,
          value: valueMajor,
          currency: pending.currency || 'USD',
          items: [
            {
              item_id: 'balance_topup',
              item_name: 'Balance Top-up',
              price: valueMajor,
              quantity: 1,
            },
          ],
        });
        markPurchaseAnalyticsSent();
      } catch (error) {
        console.error('[Analytics] Failed to parse pending deposit', error);
      }
      return;
    }

    const invoiceId = getCryptoInvoiceIdFromPaymentReturnHash(
      window.location.hash,
    );
    if (invoiceId) {
      const resolved = tryConsumeCryptoDepositAmountForInvoiceReturn(invoiceId);
      const resolvedCents = resolved?.valueInCents;
      const currency = resolved?.currency ?? 'USD';
      const valueMajor =
        resolvedCents != null && Number.isFinite(resolvedCents)
          ? centsToUsd(resolvedCents)
          : 0;

      pushGa4Event('purchase', {
        transaction_id: invoiceId,
        value: valueMajor,
        currency,
        items: [
          {
            item_id: 'balance_topup',
            item_name: 'Balance Top-up',
            price: valueMajor,
            quantity: 1,
          },
        ],
      });
      markPurchaseAnalyticsSent();
    }
  }, [isSuccess]);

  const handleClose = useCallback(() => {
    setCurrentHash('');

    if (typeof window !== 'undefined') {
      const nextUrl = new URL(window.location.href);
      nextUrl.hash = '';
      window.history.replaceState(window.history.state, '', nextUrl.toString());
    }

    navigate({
      to: location.pathname,
      search: location.search,
      hash: '',
      replace: true,
    });
  }, [location.pathname, location.search, navigate]);

  if (!isOpen) {
    return null;
  }

  const title = isSuccess
    ? t('paymentResult.successTitle')
    : t('paymentResult.failureTitle');
  const description = isSuccess
    ? t('paymentResult.successDescription')
    : t('paymentResult.failureDescription');

  return (
    <Modal
      description={description}
      isOpen
      onBeforeClose={handleClose}
      title={title}
    >
      <div className={styles.content} data-testid="payment-result-modal">
        <TextBlock color="contrast" size="l" weight="600">
          {title}
        </TextBlock>
        <TextBlock color="contrast">{description}</TextBlock>
        <Button
          className={cn(styles.button)}
          color="primary"
          data-testid="payment-result-close"
          onClick={handleClose}
          size="s"
          type="button"
          variant="filled"
        >
          {t('common.okay')}
        </Button>
      </div>
    </Modal>
  );
};
